import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import CommunityPostCard from "../components/CommunityPostCard";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, TrendingUp, Clock, Flame, Twitter, Target, Trophy, MessageSquare } from "lucide-react";
import WeeklyOverUnder from "../components/WeeklyOverUnder";
import CommunityPoll from "../components/CommunityPoll";
import { useTheme } from "../context/ThemeContext";
import AdBanner, { ADS_ENABLED } from "../components/AdBanner";


const ASSETS = ["BTC", "ETH", "SOL", "XRP", "BNB"];

function TradingSimWidget() {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    api.get("/monthly-challenge/leaderboard").then((r) => setLeaders((r.data || []).slice(0, 5))).catch(() => {});
  }, []);
  if (leaders.length === 0) return null;
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-[var(--text-primary)]">Monthly Challenge</h3>
        <Link to="/trading-simulator" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">Join →</Link>
      </div>
      <div className="space-y-1">
        {leaders.map((l, i) => {
          const up = l.return_pct >= 0;
          return (
            <Link key={l.user_id} to={"/profile/" + l.user_id} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-[var(--bg-elevated)] transition">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-sm w-5 flex-shrink-0">{i < 3 ? medals[i] : "#" + (i + 1)}</span>
                {l.avatar_url ? (
                  <img src={l.avatar_url} alt={l.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold text-xs">{l.name[0]}</span>
                  </div>
                )}
                <span className="text-sm text-[var(--text-primary)] truncate">{l.name}</span>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-mono text-xs text-[var(--text-primary)]">${l.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div className={"font-mono text-xs font-bold " + (up ? "text-green-400" : "text-red-400")}>
                  {up ? "+" : ""}{l.return_pct}%
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function TopAnalystsCard() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/community/leaderboard-v2")
      .then((r) => setLeaders(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (leaders.length === 0) return null;

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-400" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wide text-[var(--text-primary)]">Top Analysts</h3>
        </div>
      </div>
      <div className="space-y-3">
        {leaders.slice(0, 5).map((l, i) => (
          <Link to={"/profile/" + l.author_id} key={l.author_id} className="flex items-center justify-between hover:opacity-80 transition">
            <div className="flex items-center gap-2.5 min-w-0">
                            <span className="text-base w-5 flex-shrink-0">{i < 3 ? medals[i] : "#" + (i + 1)}</span>
              {l.author_avatar ? (
                <img src={l.author_avatar} alt={l.author_name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-400 font-bold text-xs">{l.author_name[0]}</span>
                </div>
              )}
              <span className="text-sm text-[var(--text-secondary)] truncate">{l.author_name}</span>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-xs font-mono font-bold text-green-400">{l.correct} correct</div>
              {l.monthly_wins > 0 && <div className="text-xs font-mono text-yellow-400">🏆 {l.monthly_wins} won</div>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [siteStats, setSiteStats] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sentiment, setSentiment] = useState(null);
  const [tagsInput, setTagsInput] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const composerRef = useRef(null);
  const [postType, setPostType] = useState("discussion");
  const [asset, setAsset] = useState("BTC");
  const [assetPrice, setAssetPrice] = useState(null);
  const [targetDate, setTargetDate] = useState("");
  const [direction, setDirection] = useState("above");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/posts?limit=50");
      setPosts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.id]);
  useEffect(() => { api.get("/stats/site").then((r) => setSiteStats(r.data)).catch(() => {}); }, []);


  useEffect(() => {
    if (postType !== "prediction") return;
    setAssetPrice(null);
    const symbolMap = { BTC: "BTCUSDT", ETH: "ETHUSDT", SOL: "SOLUSDT", XRP: "XRPUSDT", BNB: "BNBUSDT" };
    const symbol = symbolMap[asset];
    if (!symbol) return;
    fetch("https://api.binance.com/api/v3/ticker/price?symbol=" + symbol)
      .then((r) => r.json())
      .then((d) => setAssetPrice(parseFloat(d.price)))
      .catch(() => {});
  }, [asset, postType]);

const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    if (postType === "prediction" && !targetDate) {
      return toast.error("Please pick a target date");
    }
    setPosting(true);
    try {
      if (postType === "prediction") {
        await api.post("/community/predictions", {
          title, content, asset, target_date: targetDate, direction
        });
      } else {
        const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
        await api.post("/community/posts", { title, content, image_url: imageUrl, sentiment, tags });
      }
      setTitle(""); setContent(""); setImageUrl(""); setTargetDate(""); setSentiment(null); setTagsInput("");
      toast.success("Posted to the feed");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg || "Could not publish post");
    } finally { setPosting(false); }
  };

  const scopedPosts = activeTab === "mine" && user ? posts.filter((p) => p.author_id === user.id) : posts;
  const sorted = [...scopedPosts].sort((a, b) => {
    if (b.pinned !== a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (activeTab === "top") return (b.likes_count || 0) - (a.likes_count || 0);
    if (activeTab === "hot") return ((b.likes_count || 0) + (b.comments_count || 0)) - ((a.likes_count || 0) + (a.comments_count || 0));
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const shareToTwitter = (post) => {
    const text = encodeURIComponent(post.title + " — MediaCrypto Community");
    const url = encodeURIComponent("https://mediacrypto.ai/community/" + post.id);
    window.open("https://twitter.com/intent/tweet?text=" + text + "&url=" + url, "_blank", "noopener,noreferrer");
  };

  const postCount = posts.length;

  return (
    <div
      className="pt-10 pb-20"
      style={{
        backgroundImage: theme === "light"
          ? "linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.9)), url('/images/community-bg-light.png')"
          : "linear-gradient(rgba(5,5,5,0.85), rgba(5,5,5,0.9)), url('/images/community-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="max-w-[1680px] mx-auto px-6 md:px-10 pt-2 pb-2">
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[var(--text-primary)] tracking-tight">Community</h1>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">{postCount} posts from traders, builders, and researchers — predictions tracked publicly.</p>
      </div>

      <div className="max-w-[1680px] mx-auto px-6 md:px-10 mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8">

      <aside className="lg:col-span-3 space-y-5">
      <WeeklyOverUnder />
      <CommunityPoll />
      <div className="w-full flex justify-center items-center">
        <AdBanner adKey="446007b2fa20c3b18764f3aed029d17e" width={468} height={60} />
      </div>
      </aside>


        <div className="lg:col-span-7 space-y-5">



          <div ref={composerRef}>
          {user ? (
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
              {/* Post type selector */}
              <div className="flex items-center gap-2 mb-5">
                <button
                  onClick={() => setPostType("discussion")}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border rounded-full transition " +
                    (postType === "discussion" ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}
                >
                  <Send className="w-3 h-3" /> Discussion
                </button>
                <button
                  onClick={() => setPostType("prediction")}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border rounded-full transition " +
                    (postType === "prediction" ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}
                >
                  <Target className="w-3 h-3" /> Price Prediction
                </button>
              </div>

              <form onSubmit={submit}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center">
  {user.avatar_url ? (
    <img src={user.avatar_url} alt={user.name} className="w-9 h-9 object-cover" />
  ) : (
    <div className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm">
      {(user.name || user.email)[0].toUpperCase()}
    </div>
  )}
</div>
                  <span className="text-sm text-[var(--text-tertiary)] font-mono">Posting as <span className="text-[var(--text-secondary)]">{user.name}</span></span>
                </div>

                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={postType === "prediction" ? "e.g. BTC will close above today's price by July 31" : "Post title..."}
                  className="w-full bg-transparent border-b border-[var(--border-soft)] px-0 py-2 text-lg font-display font-bold focus:outline-none focus:border-yellow-400 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                />

                {postType === "prediction" && (
                  <>
                    <div className="mt-4 flex items-center gap-2 text-sm">
                      <span className="text-xs font-mono text-[var(--text-tertiary)] uppercase">Current {asset} price:</span>
                      {assetPrice ? (
                        <span className="font-mono font-bold text-yellow-400">${assetPrice.toLocaleString(undefined, { maximumFractionDigits: assetPrice < 1 ? 4 : 2 })}</span>
                      ) : (
                        <span className="font-mono text-[var(--text-tertiary)]">loading...</span>
                      )}
                    </div>
                    
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">






                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Asset</label>
                      <select value={asset} onChange={(e) => setAsset(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
                        {ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Direction</label>
                      <select value={direction} onChange={(e) => setDirection(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400">
                        <option value="above">Above current price</option>
                        <option value="below">Below current price</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase mb-1 block">Target Date</label>
                      <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
                        className="w-full bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400" />
                    </div>
                    </div>
                  </>
                )}

                {postType === "discussion" && (
                  <>
                    <div className="flex items-center gap-2 mt-4">
                      <span className="text-xs font-mono text-[var(--text-tertiary)] uppercase mr-1">Sentiment:</span>
                      {[
                        { key: "bullish", label: "Bullish", color: "border-green-500 text-green-400 bg-green-500/10" },
                        { key: "neutral", label: "Neutral", color: "border-[var(--border-soft)] text-[var(--text-tertiary)] bg-[var(--bg-elevated)]" },
                        { key: "bearish", label: "Bearish", color: "border-red-500 text-red-400 bg-red-500/10" },
                      ].map((s) => (
                        <button key={s.key} type="button" onClick={() => setSentiment(sentiment === s.key ? null : s.key)}
                          className={"px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border transition " +
                            (sentiment === s.key ? s.color : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400/40")}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <input
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="Image URL (optional)..."
                      className="w-full mt-3 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]"
                    />
                    <input
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      placeholder="Tags, comma separated (e.g. Solana, Analysis, Altcoins)..."
                      className="w-full mt-3 bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-yellow-400 placeholder:text-[var(--text-tertiary)]"
                    />
                  </>
                )}

                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={postType === "prediction" ? "Share your analysis and reasoning..." : "Share your alpha, charts, or hot takes..."}
                  rows={4}
                  className="w-full mt-4 bg-transparent text-[var(--text-secondary)] placeholder:text-[var(--text-tertiary)] focus:outline-none resize-none"
                />
                <div className="flex justify-end mt-3 pt-3 border-t border-[var(--border-soft)]">
                  <button type="submit" disabled={posting}
                    className="bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-6 py-2.5 hover:bg-yellow-500 transition disabled:opacity-50 flex items-center gap-2 text-sm">
                    {postType === "prediction" ? <Target className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    {postType === "prediction" ? "Post Prediction" : "Publish Post"}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-8 text-center">
              <p className="text-[var(--text-secondary)] mb-4 text-lg">Sign in to publish posts and join the conversation.</p>
              <div className="flex justify-center gap-3">
                <Link to="/login" className="border border-[var(--border-soft)] hover:border-yellow-400 hover:text-yellow-400 px-6 py-2 text-sm uppercase tracking-wide transition text-[var(--text-primary)]">Sign in</Link>
                <Link to="/register" className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-2 hover:bg-yellow-500 transition">Join free</Link>
              </div>
            </div>
          )}
          </div>

          {/* Discussions header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { id: "all", label: "All Posts" },
                ...(user ? [{ id: "mine", label: "My Posts" }] : []),
                { id: "hot", label: "Hot", icon: Flame },
                { id: "new", label: "New", icon: Clock },
                { id: "top", label: "Top", icon: TrendingUp },
              ].map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setActiveTab(id)}
                  className={"flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full border transition " +
                    (activeTab === id ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}>
                  {Icon && <Icon className="w-3 h-3" />} {label}
                </button>
              ))}
            </div>
          </div>

          {/* Feed */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-[var(--text-tertiary)] font-mono text-sm">Loading feed...</div>
            ) : sorted.length === 0 ? (
              <div className="text-[var(--text-tertiary)] p-8 border border-[var(--border-soft)] bg-[var(--bg-panel)] text-center">
                <p className="text-lg font-display font-bold text-[var(--text-primary)] mb-2">No posts yet.</p>
                <p className="text-sm">Be the first to start the conversation.</p>
              </div>
            ) : (
              sorted.map((p) => (
                <div key={p.id} className="relative">
                  {p.pinned && (
                    <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
                      <span className="text-xs font-mono text-yellow-400 uppercase tracking-widest">📌 Pinned</span>
                    </div>
                  )}
                  <div className={p.pinned ? "border border-yellow-400/30 bg-[var(--bg-panel)]" : ""}>
                    <CommunityPostCard post={p} onChanged={load} />
                    <div className="px-4 pb-3 flex justify-end">
                      <button onClick={() => shareToTwitter(p)}
                        className="flex items-center gap-1.5 text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                        <Twitter className="w-3 h-3" /> Share
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-2 space-y-5">
          {!user && (
            <div className="bg-yellow-400/5 border border-yellow-400/20 rounded-2xl p-6">
              <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">Join the conversation</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-4">Post your analysis, share alpha, and connect with crypto traders.</p>
              <Link to="/register" className="block text-center bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-4 py-2.5 hover:bg-yellow-500 transition text-sm">
                Create Free Account
              </Link>
              <Link to="/login" className="block text-center mt-2 text-xs font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                Already have an account? Sign in
              </Link>
            </div>
          )}

          <TopAnalystsCard />
          <TradingSimWidget />

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">Community Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="font-display font-bold text-2xl text-[var(--text-primary)]">{siteStats?.community_posts ?? "—"}</div>
                <div className="text-xs text-[var(--text-tertiary)]">Total Posts</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-yellow-400">{siteStats?.posts_this_week ?? "—"}</div>
                <div className="text-xs text-[var(--text-tertiary)]">This Week</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-[var(--text-primary)]">{siteStats?.predictions_made ?? "—"}</div>
                <div className="text-xs text-[var(--text-tertiary)]">Predictions Made</div>
              </div>
              <div>
                <div className="font-display font-bold text-2xl text-green-400">{siteStats?.avg_accuracy != null ? siteStats.avg_accuracy + "%" : "—"}</div>
                <div className="text-xs text-[var(--text-tertiary)]">Avg Accuracy</div>
              </div>
            </div>
          </div>

          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-2xl p-6">
            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-4">Community Guidelines</h3>
            <ul className="space-y-3 text-sm text-[var(--text-secondary)]">
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Be respectful and kind.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> No spam or self-promotion.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Do your own research.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Cite sources for data.</li>
              <li className="flex gap-2"><span className="text-yellow-400">+</span> Predictions are tracked publicly.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}